import { useRouter } from 'expo-router';
import { Building2, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, MessageSquare, ShieldCheck, X, XCircle } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';

import { api } from '~/api';
import { AccountShell } from '~/components/AccountShell';
import { LoadingState } from '~/components/ui';
import { todayIso, formatIsoShort, formatUtcDateTime, isBeforeSupplierDeadline } from '~/lib/dateUtils';
import { formatOrderMoney } from '~/lib/money';
import { formatExactAmount } from '~/lib/currency';
import { bookingReference, bookingStatusLabel, paymentStatusLabel, paymentWasReceived, roomAndBed } from '~/lib/bookingDisplay';
import { readTripsCache, writeTripsCache } from '~/lib/tripsCache';
import { useCurrency } from '~/stores/useCurrency';
import { useMember } from '~/stores/useMember';
import { colors, control, fontFamily, maxContentWidth, radii, space, spacing, type } from '~/ui/tokens';

type Trip = Wire.OrderSummary;
type TabKey = 'upcoming' | 'cancelledFailed' | 'past';

// A cancelled or failed booking is never "upcoming"/"past" no matter its
// dates — those two pills are reserved for bookings that actually happened
// (or will).
function isCancelledOrFailed(trip: Trip): boolean {
    return ['cancelled', 'failed', 'payment_failed', 'order_failed', 'booking_failed', 'refunded', 'refund_pending', 'rejected'].includes(trip.status);
}

// Missing/unknown checkout dates count as upcoming rather than past — a
// booking should never silently vanish from the list it's most likely to be
// looked for in.
function isPast(trip: Trip, today: string): boolean {
    return !isCancelledOrFailed(trip) && Boolean(trip.checkOut) && trip.checkOut < today;
}

function formatTripDates(trip: Trip): string {
    if (!trip.checkIn || !trip.checkOut) return 'Dates pending';
    const start = `${formatIsoShort(trip.checkIn)}, ${trip.checkIn.slice(0, 4)}`;
    const end = `${formatIsoShort(trip.checkOut)}, ${trip.checkOut.slice(0, 4)}`;
    return trip.checkIn.slice(0, 4) === trip.checkOut.slice(0, 4)
        ? `${formatIsoShort(trip.checkIn)} – ${end}`
        : `${start} – ${end}`;
}

function money(value: { amount: string; currencyCode: string } | null | undefined): string | null {
    return value ? formatExactAmount(value.amount, value.currencyCode) : null;
}

// What the guest is agreeing to, from the server's cancellation preview.
function cancelPreviewText(preview: Api.Order.Cancel.Preview | null | undefined): string {
    if (preview === undefined) return 'Checking the cancellation terms for this booking…';
    if (preview === null) return 'We couldn’t load the cancellation terms. If you cancel, any refund follows the terms agreed when you booked.';
    if (!preview.isCancellable) return 'This booking can’t be cancelled online. Contact support and we’ll help.';
    const refund = money(preview.expectedRefund);
    if (preview.refundMode === 'automatic' && refund) {
        const deadline = preview.freeCancellationBefore ? ` until ${formatUtcDateTime(preview.freeCancellationBefore)}` : '';
        return `You’re within free cancellation${deadline}. Cancelling refunds ${refund} to your original payment method. Cancel this booking?`;
    }
    if (preview.refundMode === 'manual_review') {
        return 'The free cancellation deadline has passed, so the hotel’s cancellation charge applies. Our team will review any refund and email you. Cancel this booking?';
    }
    return 'This rate is non-refundable, so cancelling will not refund your payment. Cancel this booking?';
}

function cancelledNotice(refund: Api.Order.Cancel.Refund | null): string {
    const amount = money(refund?.amount);
    if (refund?.mode === 'automatic' && refund.status === 'completed') return `Your booking has been cancelled and ${amount ?? 'your payment'} has been refunded to your original payment method.`;
    if (refund?.mode === 'automatic') return `Your booking has been cancelled. Your refund of ${amount ?? 'your payment'} has been initiated and usually arrives in 7–10 working days.`;
    if (refund?.mode === 'manual_review') return 'Your booking has been cancelled. Our team will review your refund and email you.';
    return 'Your booking has been cancelled. A confirmation email is on its way.';
}

function TripsScreen() {
    const router = useRouter();
    const isMember = useMember(state => state.isMember);
    const userEmail = useMember(state => state.user?.email ?? '');
    const openPrompt = useMember(state => state.openPrompt);
    useCurrency((state) => state.selectedCurrency);

    const [trips, setTrips] = useState<Trip[] | null>(null);
    const [tripsOwner, setTripsOwner] = useState('');
    const [failed, setFailed] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [fresh, setFresh] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>('upcoming');

    // Support ticket modal state
    const [supportTrip, setSupportTrip] = useState<Trip | null>(null);
    const [ticketSubject, setTicketSubject] = useState('');
    const [ticketMessage, setTicketMessage] = useState('');
    const [ticketSubmitting, setTicketSubmitting] = useState(false);

    // Cancellation confirmation modal state
    const [cancelTrip, setCancelTrip] = useState<Trip | null>(null);
    // undefined: loading; null: the preview could not be loaded.
    const [cancelPreview, setCancelPreview] = useState<Api.Order.Cancel.Preview | null | undefined>(undefined);

    useEffect(() => {
        if (!cancelTrip?.partnerOrderId) return;
        let active = true;
        setCancelPreview(undefined);
        api.order.cancelPreview({ partnerOrderId: cancelTrip.partnerOrderId })
            .then(res => { if (active) setCancelPreview(res.data ?? null); })
            .catch(() => { if (active) setCancelPreview(null); });
        return () => { active = false; };
    }, [cancelTrip?.partnerOrderId]);
    const [cancelling, setCancelling] = useState(false);

    const visibleTrips = tripsOwner === userEmail ? trips : null;
    const today = todayIso();
    const tabs: Array<{ key: TabKey; title: string; items: Trip[] }> = visibleTrips ? [
        { key: 'upcoming', title: 'Upcoming', items: visibleTrips.filter(trip => !isCancelledOrFailed(trip) && !isPast(trip, today)) },
        { key: 'cancelledFailed', title: 'Cancelled & Failed', items: visibleTrips.filter(isCancelledOrFailed) },
        { key: 'past', title: 'Past', items: visibleTrips.filter(trip => isPast(trip, today)) },
    ] : [];
    const activeGroup = tabs.find(tab => tab.key === activeTab) ?? null;

    const load = useCallback(() => {
        if (!userEmail) return;
        setFailed(false);
        setRefreshing(true);

        api.order
            .info()
            .then(response => {
                if (!response.data || !Array.isArray(response.data.orders)) throw new Error('Bookings unavailable');
                if (useMember.getState().user?.email !== userEmail) return;
                setTrips(response.data.orders);
                setTripsOwner(userEmail);
                setFresh(true);
                writeTripsCache(userEmail, response.data.orders);
            })
            .catch(() => {
                if (useMember.getState().user?.email !== userEmail) return;
                setFailed(true);
                setFresh(false);
            })
            .finally(() => {
                if (useMember.getState().user?.email === userEmail) setRefreshing(false);
            });
    }, [userEmail]);

    useEffect(() => {
        if (!isMember || !userEmail) {
            setTrips(null);
            setTripsOwner('');
            return;
        }

        const cached = readTripsCache(userEmail);
        setTrips(cached);
        setTripsOwner(userEmail);
        setFresh(false);
        load();
    }, [isMember, userEmail, load]);

    const MAX_ACTIVE_POLL_ATTEMPTS = 6;
    const pollAttemptsRef = useRef(0);

    // If any trip is actively processing, ping api.order.info()
    // up to MAX_ACTIVE_POLL_ATTEMPTS times (~20s total), then stop to prevent endless polling.
    useEffect(() => {
        if (!isMember || !visibleTrips || !fresh || failed) return;
        const hasActive = visibleTrips.some(trip => ['processing', 'pending_payment'].includes(trip.status));
        if (!hasActive) {
            pollAttemptsRef.current = 0;
            return;
        }

        if (pollAttemptsRef.current >= MAX_ACTIVE_POLL_ATTEMPTS) {
            return;
        }

        const timer = setTimeout(() => {
            pollAttemptsRef.current += 1;
            load();
        }, 3500);

        return () => clearTimeout(timer);
    }, [isMember, visibleTrips, fresh, failed, load]);

    const handleCreateTicket = async () => {
        if (!supportTrip || !ticketSubject.trim() || !ticketMessage.trim()) return;
        setTicketSubmitting(true);
        try {
            const res = await api.support.create({
                bookingId: supportTrip.orderId,
                partnerOrderId: supportTrip.partnerOrderId,
                subject: ticketSubject.trim(),
                initialMessage: ticketMessage.trim()
            });

            if (res.data) {
                setNotice(`Support ticket ${res.data.ticketId} created! Our team will respond shortly.`);
                setSupportTrip(null);
                setTicketSubject('');
                setTicketMessage('');
            } else {
                setNotice('Failed to create ticket. Please try again.');
            }
        } catch {
            setNotice('Could not create support ticket.');
        } finally {
            setTicketSubmitting(false);
        }
    };

    const handleConfirmCancel = async () => {
        if (!cancelTrip?.partnerOrderId) return;
        setCancelling(true);
        try {
            const res = await api.order.cancel({ partnerOrderId: cancelTrip.partnerOrderId });
            if (res.data?.cancelled) {
                setNotice(cancelledNotice(res.data.refund ?? null));
                load();
            } else {
                setNotice('We couldn’t cancel this booking. Please try again or contact support.');
            }
        } catch {
            setNotice('We couldn’t cancel this booking. Please try again or contact support.');
        } finally {
            setCancelling(false);
            setCancelTrip(null);
        }
    };

    return (
        <AccountShell active="bookings">
            <View style={styles.body}>
                <Text style={styles.heading}>Your trips</Text>

                {!isMember ? (
                    <View style={styles.panel}>
                        <View style={styles.icon}><Building2 size={24} color={colors.brand} /></View>
                        <Text style={styles.title}>Sign in to see your trips</Text>
                        <Text style={styles.sub}>
                            Your SuiteScanner+ bookings are tied to your account. Sign in free to view them.
                        </Text>
                        <Pressable accessibilityRole="button" onPress={openPrompt} style={styles.cta}>
                            <Text style={styles.ctaText}>Sign in</Text>
                        </Pressable>
                    </View>
                ) : visibleTrips === null && !failed ? (
                    <View style={styles.panel}>
                        <LoadingState label="Loading your bookings…" />
                    </View>
                ) : failed && (!visibleTrips || visibleTrips.length === 0) ? (
                    <View style={styles.panel}>
                        <View style={styles.icon}><XCircle size={24} color={colors.brand} /></View>
                        <Text style={styles.title}>Couldn’t load your trips</Text>
                        <Text style={styles.sub}>We couldn’t reach the booking service — try again in a moment.</Text>
                        <Pressable accessibilityRole="button" onPress={load} style={styles.cta}>
                            <Text style={styles.ctaText}>Retry</Text>
                        </Pressable>
                    </View>
                ) : visibleTrips?.length === 0 ? (
                    <View style={styles.panel}>
                        <View style={styles.icon}><Building2 size={24} color={colors.brand} /></View>
                        <Text style={styles.title}>No trips yet</Text>
                        <Text style={styles.sub}>When you book a stay, it appears here with its confirmation details.</Text>
                        <Pressable accessibilityRole="button" onPress={() => router.push('/search')} style={styles.cta}>
                            <Text style={styles.ctaText}>Browse hotels</Text>
                        </Pressable>
                    </View>
                ) : (
                    <>
                        {notice ? <Text style={styles.notice}>{notice}</Text> : null}
                        {!fresh ? <View style={styles.cacheNotice}><Text style={styles.cacheNoticeText}>{failed ? 'Couldn’t refresh. Showing your last saved trip status.' : 'Showing your last saved trip status while we check for updates.'}</Text>{failed ? <Pressable accessibilityRole="button" onPress={load}><Text style={styles.cacheRetry}>Retry</Text></Pressable> : null}</View> : refreshing ? <Text style={styles.refreshNote}>Checking for updates…</Text> : null}

                        <View style={styles.tabRow}>
                            {tabs.map(tab => (
                                <Pressable
                                    key={tab.key}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: tab.key === activeTab }}
                                    onPress={() => setActiveTab(tab.key)}
                                    style={[styles.tabPill, tab.key === activeTab && styles.tabPillActive]}
                                >
                                    <Text style={[styles.tabPillText, tab.key === activeTab && styles.tabPillTextActive]}>
                                        {tab.title} ({tab.items.length})
                                    </Text>
                                </Pressable>
                            ))}
                        </View>

                        {activeGroup && activeGroup.items.length > 0 ? (
                            <View style={styles.list}>{activeGroup.items.map(trip => <TripRow
                                key={trip.partnerOrderId || trip.orderId}
                                trip={trip}
                                allowCancel={fresh}
                                onView={() => router.push({ pathname: '/success', params: { orderId: trip.orderId && Number(trip.orderId) > 0 ? String(trip.orderId) : '', partnerOrderId: trip.partnerOrderId || '' } })}
                                onCancel={() => setCancelTrip(trip)}
                                onOpenSupport={() => {
                                    setSupportTrip(trip);
                                    setTicketSubject(
                                        trip.orderId && Number(trip.orderId) > 0
                                            ? `Inquiry regarding booking #${trip.orderId} (${trip.hotelName})`
                                            : `Inquiry regarding booking (${trip.hotelName})`
                                    );
                                }}
                            />)}</View>
                        ) : (
                            <View style={styles.emptyTab}>
                                <Text style={styles.sub}>
                                    {activeTab === 'upcoming' ? "You don't have any upcoming bookings."
                                        : activeTab === 'past' ? "You don't have any past stays yet."
                                            : 'No cancelled or failed bookings.'}
                                </Text>
                            </View>
                        )}

                        <Text style={styles.reservationNote}>
                            The number shown is your SuiteScanner booking ID. The hotel’s own Reservation ID is
                            issued closer to check-in and emailed to you — the reservation is held under our
                            partner’s name at the property.
                        </Text>
                    </>
                )}
            </View>

            {/* Cancel Booking Modal */}
            {cancelTrip ? (
                <Modal visible transparent animationType="fade" onRequestClose={() => !cancelling && setCancelTrip(null)}>
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalCard}>
                            <Text style={styles.modalTitle}>Cancel booking?</Text>
                            <Text style={[styles.modalSub, { marginTop: space.related }]}>
                                {cancelPreviewText(cancelPreview)}
                            </Text>
                            <View style={[styles.modalFooter, { marginTop: space.card }]}>
                                <Pressable onPress={() => setCancelTrip(null)} disabled={cancelling} style={styles.cancelModalBtn}>
                                    <Text style={styles.cancelModalText}>Keep booking</Text>
                                </Pressable>
                                <Pressable onPress={handleConfirmCancel} disabled={cancelling || cancelPreview?.isCancellable === false} style={[styles.submitModalBtn, (cancelling || cancelPreview?.isCancellable === false) && styles.submitDisabled]}>
                                    {cancelling ? <ActivityIndicator size="small" color={colors.white} /> : <Text style={styles.submitModalText}>Cancel</Text>}
                                </Pressable>
                            </View>
                        </View>
                    </View>
                </Modal>
            ) : null}

            {/* Support Ticket Modal */}
            {supportTrip ? (
                <Modal visible transparent animationType="fade">
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalCard}>
                            <View style={styles.modalHeader}>
                                <View>
                                    <Text style={styles.modalTitle}>Get Support for Booking</Text>
                                    <Text style={styles.modalSub}>
                                        {supportTrip.orderId && Number(supportTrip.orderId) > 0 ? `#${supportTrip.orderId} · ` : ''}{supportTrip.hotelName}
                                    </Text>
                                </View>
                                <Pressable onPress={() => setSupportTrip(null)}>
                                    <X size={20} color={colors.ink} />
                                </Pressable>
                            </View>

                            <View style={styles.modalForm}>
                                <Text style={styles.fieldLabel}>Subject</Text>
                                <TextInput
                                    value={ticketSubject}
                                    onChangeText={setTicketSubject}
                                    placeholder="What do you need help with?"
                                    style={styles.formInput}
                                />

                                <Text style={styles.fieldLabel}>Message</Text>
                                <TextInput
                                    value={ticketMessage}
                                    onChangeText={setTicketMessage}
                                    placeholder="Describe your question or issue in detail…"
                                    multiline
                                    style={[styles.formInput, { height: 100, textAlignVertical: 'top' }]}
                                />
                            </View>

                            <View style={styles.modalFooter}>
                                <Pressable onPress={() => setSupportTrip(null)} style={styles.cancelModalBtn}>
                                    <Text style={styles.cancelModalText}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={handleCreateTicket}
                                    disabled={ticketSubmitting || !ticketMessage.trim() || !ticketSubject.trim()}
                                    style={[
                                        styles.submitModalBtn,
                                        (!ticketMessage.trim() || !ticketSubject.trim() || ticketSubmitting) && styles.submitDisabled
                                    ]}
                                >
                                    {ticketSubmitting ? (
                                        <ActivityIndicator size="small" color={colors.white} />
                                    ) : (
                                        <Text style={styles.submitModalText}>Submit Ticket</Text>
                                    )}
                                </Pressable>
                            </View>
                        </View>
                    </View>
                </Modal>
            ) : null}

        </AccountShell>
    );
}

function TripRow({
    trip,
    allowCancel,
    onView,
    onCancel,
    onOpenSupport
}: {
    trip: Trip;
    allowCancel: boolean;
    onView: () => void;
    onCancel: () => void;
    onOpenSupport: () => void;
}) {
    const { width } = useWindowDimensions();
    const compact = width < 700;
    const [expanded, setExpanded] = useState(false);
    const cancelled = ['cancelled', 'refunded'].includes(trip.status);
    const failed = ['failed', 'payment_failed', 'order_failed', 'booking_failed', 'rejected'].includes(trip.status);
    const refundPending = trip.status === 'refund_pending';
    const awaitingPayment = ['pending', 'pending_payment'].includes(trip.status);
    const processing = trip.status === 'processing';
    const confirmed = ['completed', 'confirmed'].includes(trip.status);
    const pastStay = Boolean(trip.checkOut) && trip.checkOut < todayIso();
    const reference = bookingReference(trip.orderId, trip.status);
    const statusLabel = bookingStatusLabel(trip.status);
    const paymentLabel = paymentStatusLabel(trip.paymentStatus);
    const room = roomAndBed(trip.roomName);

    return (
        <View style={styles.rowCard}>
            <View style={[styles.tripHeader, compact && styles.tripHeaderCompact]}>
                <View style={[styles.tripIdentity, compact && styles.tripIdentityCompact]}>
                    <Text style={styles.hotel} numberOfLines={2}>{trip.hotelName || 'Hotel details pending'}</Text>
                    <Text style={styles.city}>{cityFromAddress(trip.hotelAddress)}</Text>
                </View>
                <View style={[styles.statusBadge, (cancelled || failed || refundPending) && styles.statusBadgeMuted, (processing || awaitingPayment) && styles.statusBadgeProcessing]}>
                    {confirmed ? <CheckCircle2 size={16} color={colors.green} /> : failed || cancelled || refundPending ? <XCircle size={16} color={colors.inkSoft} /> : null}
                    <Text style={[styles.statusBadgeText, (cancelled || failed || refundPending) && styles.statusMuted, (processing || awaitingPayment) && styles.statusProcessing]}>{statusLabel}</Text>
                </View>
            </View>
            <View style={styles.tripDates}><CalendarDays size={16} color={colors.inkFaint} /><Text style={styles.meta}>{formatTripDates(trip)}</Text></View>
            <Pressable accessibilityRole="button" accessibilityLabel={`Review details for ${trip.hotelName || 'booking'}`} accessibilityState={{ expanded }} onPress={() => setExpanded(value => !value)} style={styles.expandBtn}>
                <Text style={styles.expandBtnText}>{expanded ? 'Hide details' : 'Review details'}</Text>
                {expanded ? <ChevronUp size={16} color={colors.brand} /> : <ChevronDown size={16} color={colors.brand} />}
            </Pressable>
            {expanded ? <View style={styles.tripDetails}>
                <View style={styles.detailItem}><Text style={styles.detailLabel}>Payment</Text><Text style={styles.detailValue}>{paymentLabel}</Text></View>
                <View style={styles.detailItem}><Text style={styles.detailLabel}>Total</Text><Text style={styles.detailValue}>{formatOrderMoney(trip)}</Text></View>
                {reference ? <View style={styles.detailItem}><Text style={styles.detailLabel}>Booking ID</Text><Text style={styles.detailValue}>{reference}</Text></View> : null}
                {room.room ? <View style={styles.detailItem}><Text style={styles.detailLabel}>Room</Text><Text style={styles.detailValue}>{room.room}</Text></View> : null}
                {room.bed ? <View style={styles.detailItem}><Text style={styles.detailLabel}>Bed</Text><Text style={styles.detailValue}>{room.bed}</Text></View> : null}
                <View style={styles.detailItem}><Text style={styles.detailLabel}>Guests</Text><Text style={styles.detailValue}>{trip.roomCount} {trip.roomCount === 1 ? 'room' : 'rooms'} · {trip.guestCount} {trip.guestCount === 1 ? 'guest' : 'guests'}</Text></View>
                {processing ? <Text style={styles.processingNote}>{paymentWasReceived(trip.paymentStatus, Boolean(trip.paymentAmount)) ? 'We’ve received your payment and are confirming the reservation with the hotel.' : 'Payment and hotel confirmation are still in progress.'}</Text> : null}
                {failed ? <Text style={styles.processingNote}>This reservation could not be confirmed. View booking for the latest details.</Text> : null}
                {!failed && !cancelled && !refundPending ? <View style={styles.policyRow}><ShieldCheck size={15} color={isBeforeSupplierDeadline(trip.freeCancellationBefore) ? colors.green : colors.inkFaint} /><Text style={styles.policyText}>{processing || awaitingPayment ? 'Cancellation policy available after confirmation' : trip.freeCancellationBefore ? (isBeforeSupplierDeadline(trip.freeCancellationBefore) ? `Free cancellation until ${formatUtcDateTime(trip.freeCancellationBefore)}` : `Free cancellation ended ${formatUtcDateTime(trip.freeCancellationBefore)}`) : 'Non-refundable'}</Text></View> : null}
                <View style={[styles.detailActions, compact && styles.detailActionsCompact]}>
                    <Pressable accessibilityRole="button" onPress={onView} disabled={!trip.orderId && !trip.partnerOrderId} style={[styles.viewBtn, !trip.orderId && !trip.partnerOrderId && styles.cancelBtnBusy]}><Text style={styles.viewBtnText}>View booking</Text></Pressable>
                    {allowCancel && confirmed && trip.partnerOrderId && !pastStay ? <Pressable accessibilityRole="button" onPress={onCancel} style={styles.supportBtn}><X size={13} color={colors.brand} /><Text style={styles.supportBtnText}>Cancel booking</Text></Pressable> : null}
                    <Pressable accessibilityRole="button" onPress={onOpenSupport} style={styles.supportBtn}><MessageSquare size={13} color={colors.brand} /><Text style={styles.supportBtnText}>Need help?</Text></Pressable>
                </View>
            </View>
            : null}
        </View>
    );
}

function cityFromAddress(address?: string): string {
    if (!address?.trim()) return 'City to be confirmed';
    const parts = address.split(',').map(part => part.trim()).filter(Boolean);
    const country = /^(united arab emirates|uae|france|india|united states|united kingdom)$/i;
    const last = parts.length > 1 && country.test(parts[parts.length - 1]) ? parts[parts.length - 2] : parts[parts.length - 1];
    return last.replace(/\s*\b\d{4,}\b.*$/, '').trim() || 'City to be confirmed';
}

const styles = StyleSheet.create({
    screen: { flex: 1, backgroundColor: colors.cream },
    content: { paddingBottom: 0 },
    body: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor },
    heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink, marginBottom: space.card },
    panel: {
        backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line,
        padding: space.cardLarge, alignItems: 'center', gap: space.related
    },
    icon: { width: 56, height: 56, borderRadius: radii.full, backgroundColor: colors.skyTint, alignItems: 'center', justifyContent: 'center', marginBottom: space.related },
    title: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink },
    sub: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkFaint, textAlign: 'center' },
    cta: { marginTop: space.compact, backgroundColor: colors.brand, paddingVertical: space.compact, paddingHorizontal: space.card, borderRadius: radii.md },
    ctaText: { fontFamily: fontFamily.semibold, ...control.secondaryCta, color: colors.white },
    notice: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.brand, marginBottom: space.related },
    cacheNotice: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: space.related, backgroundColor: colors.skyTint, borderRadius: radii.md, padding: space.internal, marginBottom: space.internal },
    cacheNoticeText: { flex: 1, minWidth: 210, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
    cacheRetry: { fontFamily: fontFamily.semibold, ...type.metadata, color: colors.brand },
    refreshNote: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint, marginBottom: space.related },
    tabRow: { flexDirection: 'row', flexWrap: 'wrap', gap: space.related, marginBottom: space.card },
    tabPill: { paddingVertical: space.related, paddingHorizontal: space.internal, borderRadius: radii.full, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card },
    tabPillActive: { backgroundColor: colors.brand, borderColor: colors.brand },
    tabPillText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
    tabPillTextActive: { color: colors.white },
    emptyTab: { paddingVertical: space.component, alignItems: 'center' },
    list: { gap: space.card },
    statusProcessing: { color: colors.brand },
    statusMuted: { color: colors.inkFaint },
    hotel: { fontFamily: fontFamily.bold, fontSize: 21, lineHeight: 27, color: colors.ink },
    processingNote: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft, marginTop: space.micro },
    meta: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
    policyRow: { flexDirection: 'row', alignItems: 'center', gap: space.related, marginTop: space.micro },
    policyText: { flex: 1, fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft },
    supportBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: space.related, paddingVertical: space.compact, paddingHorizontal: spacing(1.25), borderRadius: radii.md, backgroundColor: colors.skyTint },
    supportBtnText: { fontFamily: fontFamily.semibold, ...control.utilityCta, color: colors.brand },
    viewBtn: { backgroundColor: colors.brand, borderRadius: radii.md, paddingVertical: space.compact, paddingHorizontal: space.compact, alignItems: 'center' },
    viewBtnText: { fontFamily: fontFamily.semibold, ...control.utilityCta, color: colors.white },
    cancelBtnBusy: { opacity: 0.6 },
    rowCard: { width: '100%', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, padding: space.card, gap: space.related },
    tripHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.internal },
    tripHeaderCompact: { flexDirection: 'column', alignItems: 'flex-start' },
    tripIdentity: { flex: 1, minWidth: 180, gap: space.micro },
    tripIdentityCompact: { flexGrow: 0, width: '100%' },
    city: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    statusBadge: { flexDirection: 'row', alignItems: 'center', gap: space.micro, backgroundColor: '#EAF7EF', borderRadius: radii.full, paddingHorizontal: space.related, paddingVertical: space.micro },
    statusBadgeMuted: { backgroundColor: colors.lineSoft },
    statusBadgeProcessing: { backgroundColor: colors.skyTint },
    statusBadgeText: { fontFamily: fontFamily.semibold, ...type.metadata, color: colors.green },
    tripDates: { flexDirection: 'row', alignItems: 'center', gap: space.related },
    tripDetails: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.internal, gap: space.related },
    detailItem: { flexDirection: 'row', justifyContent: 'space-between', gap: space.internal },
    detailLabel: { flex: 1, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
    detailValue: { flex: 2, fontFamily: fontFamily.medium, ...type.metadata, color: colors.ink, textAlign: 'right' },
    detailActions: { flexDirection: 'row', flexWrap: 'wrap', gap: space.related, marginTop: space.related },
    detailActionsCompact: { flexDirection: 'column' },
    expandBtn: { flexDirection: 'row', alignItems: 'center', gap: space.micro, paddingVertical: spacing(0.375) },
    expandBtnText: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.brand },
    reservationNote: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint, marginTop: space.component },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: space.internal },
    modalCard: { width: '100%', maxWidth: 480, backgroundColor: colors.card, borderRadius: radii.lg, padding: space.card },
    modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.internal },
    modalTitle: { fontFamily: fontFamily.bold, fontSize: type.subsectionTitle.fontSize, color: colors.ink },
    modalSub: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint },
    modalForm: { gap: space.internal },
    fieldLabel: { fontFamily: fontFamily.semibold, ...control.inputLabel, color: colors.ink, marginBottom: space.related },
    formInput: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: space.compact, paddingVertical: space.compact, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink, backgroundColor: colors.cream },
    modalFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: space.compact, marginTop: space.component },
    cancelModalBtn: { borderWidth: 1, borderColor: colors.line, paddingVertical: space.related, paddingHorizontal: space.internal, borderRadius: radii.md },
    cancelModalText: { fontFamily: fontFamily.medium, ...control.secondaryCta, color: colors.inkSoft },
    submitModalBtn: { backgroundColor: colors.brand, paddingVertical: space.related, paddingHorizontal: space.component, borderRadius: radii.md, alignItems: 'center', justifyContent: 'center' },
    submitDisabled: { opacity: 0.5 },
    submitModalText: { fontFamily: fontFamily.semibold, ...control.secondaryCta, color: colors.white }
});

export default TripsScreen;
